import React, { Key, ReactNode, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ExtendedTable, Header, PermissionWrapper, Subheader, TableSettingsToolbar } from '@app/components'
import { ContentLayout } from '@app/layouts'
import { LntButton, LntDropdown } from '@app/components/base'
import { CopyArchivesListModal } from '@app/modals'

import { getExportFilesDropdownMenu, isEmptyString } from '@app/utils'
import { exportArchivesItemFile, exportCompareArchivesFile } from '@app/utils/export'

import {
  useExtendedNavigate,
  usePaginationQueryParams,
  useErrorRedirect,
  useToggle,
  useNotification,
  useTableSearch,
  useTableSetting,
  useTableSize
} from '@app/hooks'

import { FILE_TYPES } from '@app/api/constants'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORINGS_LISTS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'

import { copyArchivesItem, getListsArchivalItem, getListsArchivalItemProducts } from '@app/api/routes/monitoringLists'

import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { getColumns, adaptRegistryArchiveListTableData } from './lib'
import { columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { RegistryPageInnerArchivesProps } from './types'
import type { CopyArchivesItem, GetListsItemProducts } from '@app/api/routes/monitoringLists/types'

const columns = getColumns()

export const RegistryPageArchivesInner: React.FC<RegistryPageInnerArchivesProps> = ({ id }) => {
  const queryClient = useQueryClient()
  const navigate = useExtendedNavigate()
  const notification = useNotification()
  const [title, setTitle] = useState('')

  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const [rowSelectionKeys, setRowSelectionKeys] = useState<Key[]>([])

  const { size, onSizeToggle } = useTableSize(['middle', 'large'])

  const copyListModalToggle = useToggle()

  const buttonLoadingExportToggle = useToggle()

  const listItemProductsParams = useMemo(
    () => ({ ...paginationParams, id }),
    [paginationParams, id]
  ) as GetListsItemProducts

  const { data: dataListsArchivalItem, isError: isErrorDataListArchivesItem } = useQuery({
    retry: false,
    queryKey: [MONITORINGS_LISTS_KEY.archivesListItem, id],
    queryFn: async () => await getListsArchivalItem(id)
  })

  const { data: registryArchiveListTableData, isLoading: isDataListsArchivalItemProductsLoading } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts, listItemProductsParams],
    queryFn: async () => await getListsArchivalItemProducts(listItemProductsParams),
    select: registryArchiveListData => adaptRegistryArchiveListTableData(registryArchiveListData)
  })

  const copyArchivesItemMutation = useMutation({
    mutationFn: copyArchivesItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.archivesListItems] })

      notification.success({
        message: 'Список успешно скопирован.'
      })
    }
  })

  const handleNavigateToRegistryPage = (): void => navigate(-1)

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setRowSelectionKeys(selectedKeys)
  }

  const handleCopyList = (data: CopyArchivesItem): void => {
    copyArchivesItemMutation.mutate(
      { ...data, archiveMonitoringListId: id },
      { onSettled: copyListModalToggle.setUntoggled }
    )
  }

  const handleExportArchivesItem = async (): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Архивный_список'

    try {
      await exportArchivesItemFile(fileName, { id, file: FILE_TYPES.xlsx })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const handleCompareDownloadFile = (): void => {
    const fileName = 'Сравнение'

    exportCompareArchivesFile(fileName, { id, file: FILE_TYPES.xlsx })
  }

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Архивный список',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportArchivesItem
  )

  const subheaderToolbar: ReactNode[] = [
    <PermissionWrapper
      key='compare'
      requiredPermissions={ [PERMISSION.exportMonitoringList] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ handleCompareDownloadFile }
      >
        Сравнить
      </LntButton>
    </PermissionWrapper>,

    <PermissionWrapper
      key='copy'
      requiredPermissions={ [PERMISSION.createAndCopyMonitoringList] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ copyListModalToggle.setToggled }
      >
        Копировать
      </LntButton>
    </PermissionWrapper>,

    <PermissionWrapper
      key='export-list'
      requiredPermissions={ [PERMISSION.exportMonitoringList] }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ exportDropdownItems }
      >
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          loading={ buttonLoadingExportToggle.value }
        >
          Экспорт списка
        </LntButton>
      </LntDropdown>
    </PermissionWrapper>
  ]

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.registryPageArchivesInner, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredListsItemProductsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: registryArchiveListTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = (
    <TableSettingsToolbar
      tableSizeButtonProps={ { active: size === SIZE.large, onClick: onSizeToggle } }
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  )

  useErrorRedirect(isErrorDataListArchivesItem)

  useEffect(
    function updateTitle () {
      if (dataListsArchivalItem && isEmptyString(title)) {
        const monitoringTypeName: string = dataListsArchivalItem?.monitoringType?.name ?? ''
        const storeName: string | undefined = dataListsArchivalItem?.store?.name
        const clusterName: string | undefined = dataListsArchivalItem?.cluster?.name
        const clustersGroupName: string | undefined = dataListsArchivalItem?.clustersGroup?.name

        setTitle(`${monitoringTypeName}, ${storeName ?? clusterName ?? clustersGroupName ?? ''}`)
      }
    },
    [dataListsArchivalItem]
  )

  return (
    <>
      <Header controlProps={ { title, onPushBack: handleNavigateToRegistryPage } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <ExtendedTable
          size={ size }
          loading={ isDataListsArchivalItemProductsLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredListsItemProductsDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            selectedRowKeys: rowSelectionKeys,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: registryArchiveListTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <CopyArchivesListModal
        open={ copyListModalToggle.value }
        onCancel={ copyListModalToggle.setUntoggled }
        onSubmit={ handleCopyList }
        title='Копировать список в'
      />
    </>
  )
}
